import type { Wine } from '@/types';

// ── Internal computation enum ──

export type MaturityCode =
  | 'HOLD'
  | 'DRINK_OR_HOLD'
  | 'DRINK'
  | 'DRINK_SOON'
  | 'PAST_PEAK'
  | 'UNKNOWN';

/**
 * Core maturity computation — single source of truth.
 *
 * Five-level system based on position within the drinking window:
 * - Both null/invalid → UNKNOWN
 * - Only drinkUntil set → past it: PAST_PEAK, otherwise: DRINK
 * - Only drinkFrom set → before it: HOLD, otherwise: DRINK
 * - Both set, before window → HOLD
 * - Both set, after window → PAST_PEAK
 * - Inside window (span > 3 years):
 *     First 50% → DRINK_OR_HOLD
 *     Middle 30% (50-80%) → DRINK
 *     Final 20% (80-100%) → DRINK_SOON
 * - Inside window (span <= 3 years, short-window safeguard):
 *     First 80% → DRINK
 *     Final 20% → DRINK_SOON
 */
export function computeMaturityCode(
  drinkFrom: number | string | null | undefined,
  drinkUntil: number | string | null | undefined,
): MaturityCode {
  const year = new Date().getFullYear();
  const from = Number(drinkFrom);
  const to = Number(drinkUntil);
  const hasFrom = !isNaN(from) && from > 0;
  const hasTo = !isNaN(to) && to > 0;

  if (!hasFrom && !hasTo) return 'UNKNOWN';
  if (!hasFrom) return year > to ? 'PAST_PEAK' : 'DRINK';
  if (!hasTo) return year < from ? 'HOLD' : 'DRINK';

  // Both set
  if (year < from) return 'HOLD';
  if (year > to) return 'PAST_PEAK';

  // Inside window — subdivide by percentage
  const span = to - from;
  if (span <= 3) {
    // Short window: skip DRINK_OR_HOLD
    const elapsed = year - from;
    return elapsed < span * 0.80 ? 'DRINK' : 'DRINK_SOON';
  }

  const progress = (year - from) / span;
  if (progress < 0.50) return 'DRINK_OR_HOLD';
  if (progress < 0.80) return 'DRINK';
  return 'DRINK_SOON';
}

// ── Format variants ──

const LABEL_MAP: Record<MaturityCode, string> = {
  HOLD: 'Ripening',
  DRINK_OR_HOLD: 'Hold or Sip',
  DRINK: 'Peak',
  DRINK_SOON: 'Fading',
  PAST_PEAK: 'Tired',
  UNKNOWN: 'Unknown',
};

/** Human-readable label */
export function getMaturityLabel(
  drinkFrom: number | string | null | undefined,
  drinkUntil: number | string | null | undefined,
): string {
  return LABEL_MAP[computeMaturityCode(drinkFrom, drinkUntil)];
}

/** Typed for Wine['maturity'] */
export function getMaturityForWine(
  drinkFrom: number | string | null | undefined,
  drinkUntil: number | string | null | undefined,
): Wine['maturity'] {
  const code = computeMaturityCode(drinkFrom, drinkUntil);
  return LABEL_MAP[code] as Wine['maturity'];
}

const KEBAB_MAP: Record<MaturityCode, string> = {
  HOLD: 'hold',
  DRINK_OR_HOLD: 'drink-or-hold',
  DRINK: 'drink',
  DRINK_SOON: 'drink-soon',
  PAST_PEAK: 'past-peak',
  UNKNOWN: 'unknown',
};

/** Kebab → human-readable label lookup */
const KEBAB_LABEL_MAP: Record<string, string> = {
  'hold': 'Ripening',
  'drink-or-hold': 'Hold or Sip',
  'drink': 'Peak',
  'drink-soon': 'Fading',
  'past-peak': 'Tired',
  'unknown': 'Unknown',
};

export function getMaturityLabelFromKebab(kebab: string): string {
  return KEBAB_LABEL_MAP[kebab] || kebab;
}

/** Kebab-case for RC UI Set */
export function getMaturityKebab(
  drinkFrom: number | string | null | undefined,
  drinkUntil: number | string | null | undefined,
): string {
  return KEBAB_MAP[computeMaturityCode(drinkFrom, drinkUntil)];
}

const EMOJI_MAP: Record<MaturityCode, string> = {
  HOLD: '🔒 Ripening',
  DRINK_OR_HOLD: '📈 Hold or Sip',
  DRINK: '⭐ Peak',
  DRINK_SOON: '⏰ Fading',
  PAST_PEAK: '📉 Tired',
  UNKNOWN: 'Unknown',
};

/** Emoji-prefixed label */
export function getMaturityEmoji(
  drinkFrom: number | string | null | undefined,
  drinkUntil: number | string | null | undefined,
): string {
  return EMOJI_MAP[computeMaturityCode(drinkFrom, drinkUntil)];
}

// ── Sort rank (for cellar sort-by-maturity) ──

const RANK_MAP: Record<MaturityCode, number> = {
  DRINK_SOON: 0,     // most urgent first
  DRINK: 1,          // at peak
  DRINK_OR_HOLD: 2,  // can drink, no rush
  HOLD: 3,           // not ready
  PAST_PEAK: 4,      // deprioritised
  UNKNOWN: 5,        // no data
};

/** Numeric rank for sorting: Drink Soon (0) → … → Unknown (5) */
export function getMaturityRank(
  drinkFrom: number | string | null | undefined,
  drinkUntil: number | string | null | undefined,
): number {
  return RANK_MAP[computeMaturityCode(drinkFrom, drinkUntil)];
}
